import { memo } from "react";
import { motion } from "motion/react";
import { Plus } from "lucide-react";
import type { ProductCardViewModel } from "../utils/productViewMappers";

type ProductListItemV2Props = {
  model: ProductCardViewModel;
  onAdd: () => void;
};

function listPillClass(variant: ProductCardViewModel["listStockPill"]["variant"]) {
  if (variant === "out") return "bg-destructive text-destructive-foreground";
  if (variant === "low") return "bg-chart-4/20 text-chart-4";
  return "bg-muted text-muted-foreground";
}

function ProductListItemV2Inner({ model, onAdd }: ProductListItemV2Props) {
  const { listStockPill } = model;

  return (
    <motion.div
      data-product-id={model.id}
      className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 transition-colors hover:border-primary hover:bg-accent/30 [@media(pointer:coarse)]:min-h-[80px] [@media(pointer:coarse)]:py-2.5 [@media(pointer:coarse)]:text-base"
    >
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-sm font-medium text-foreground sm:text-base">{model.name}</h3>
        <p className="truncate text-xs text-muted-foreground sm:text-sm">{model.listSubtitle}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2.5 sm:gap-3">
        {model.unitPriceLabel ? (
          <p className="whitespace-nowrap text-sm font-semibold text-primary sm:text-base">{model.unitPriceLabel}</p>
        ) : (
          <p className="whitespace-nowrap text-sm text-muted-foreground">…</p>
        )}

        <span
          className={`shrink-0 rounded-md px-2 py-0.5 text-[11px] font-medium sm:text-xs ${listPillClass(
            listStockPill.variant,
          )}`}
        >
          {listStockPill.text}
        </span>
        {model.lowStockIndicator ? <span className="hidden text-[11px] font-medium text-chart-4 sm:inline">Low</span> : null}

        <motion.button
          type="button"
          whileTap={{ scale: 0.9 }}
          onClick={(e) => {
            e.stopPropagation();
            if (!model.outOfStock) void onAdd();
          }}
          disabled={model.outOfStock}
          className="rounded-md bg-primary p-2 text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={`Add ${model.name}`}
        >
          <Plus className="h-5 w-5" />
        </motion.button>
      </div>
    </motion.div>
  );
}

export const ProductListItemV2 = memo(ProductListItemV2Inner);
